/******************************************
*  Author : Riccardo Di Dato
*  Created On : Fri Apr 29 2022
*  File : utils.ts
*******************************************/
import { InvalidInputError } from '../Errors/InvalidInputError.js';
import { isArray, isDefined, isFunction, isObject, isString } from '../TypeChecking/index.js';
import { hasOwnProperty } from '../Utils/index.js';
function isLocationDefinition(t) {
    return isObject(t) && hasOwnProperty(t, "url") && hasOwnProperty(t, "params") && isObject(t.params);
}
/**
 * Questo metodo consente di aggiungere parametri in query string ad un determinato url.
 * In caso di parametri duplicati questi vengono sovrascritti
 *
 * @author Riccardo Di Dato (shatsu)
 * @author Lorenzo Muscariello (lwbu)
 *
 * @param url url da modificare
 * @param params coppie chiave valore di parametri da estrarre
 * @return Url modificato (con i parametri aggiunti)
 * @example
 * ```
 *          var modUrl = addParamsToUrl("http://sito.it/miopath",{p1: "valore",p2:"valore2"});
 *          // modUrl = http://sito.it/mipath?p1=valore&p2=valore2
 * ```
 */
export function addParamsToUrl(url, params) {
    if (isObject(params)) {
        const locDef = urlSep(url);
        Object.keys(params).forEach(key => {
            locDef.params[key] = params[key];
        });
        return urlCombine(locDef);
    }
    else {
        return url;
    }
}
/**
 * A partire da un url genera un oggetto {@link LocationDefinition}.
 *
 * @author Riccardo Di Dato (shatsu)
 * @author Lorenzo Muscariello (lwbu)
 * @param url l'url da scomporre
 * @return url / parametri scomposti
 * @throws {InvalidInputError} Se url non è una stringa (js only)
 */
export function urlSep(url) {
    if (!isString(url)) {
        throw new InvalidInputError("Invalid input given to urlSep: must be a string");
    }
    const rval = {
        url: "",
        params: {}
    };
    const split = url.split("?", 2);
    rval.url = split[0];
    if (split.length > 1) {
        rval.params = split[1].split("&").reduce((carry, param) => {
            const s = param.split("=");
            if (s.length === 2) {
                carry[decodeURIComponent(s[0])] = decodeURIComponent(s[1]);
            }
            return carry;
        }, {});
    }
    return rval;
}
/**
 * Combina l'url in ingresso (privo di parametri in query string) con i parametri in ingresso.
 *
 * @author Riccardo Di Dato (shatsu)
 * @author Lorenzo Muscariello (lwbu)
 *
 * @param url l'url da comporre
 * @param params parametri da applicare alla query string
 * @return url composto
 */
export function urlCombine(url, params) {
    if (isLocationDefinition(url)) {
        return urlCombine(url.url, url.params);
    }
    else {
        const qString = Object.keys(params).map((key) => {
            return encodeURIComponent(key) + "=" + encodeURIComponent(params[key]);
        }).join("&");
        return url + (qString != "" ? "?" + qString : "");
    }
}
function p_execIfFunction(fun) {
    if (isFunction(fun)) {
        try {
            return fun.call();
        }
        catch (e) {
            return null;
        }
    }
    else {
        return fun;
    }
}
function p_addParam(acc, key, val, prefix) {
    const useval = p_execIfFunction(val);
    const usekey = prefix + key;
    if (isObject(val)) {
        Object.keys(val).forEach((k) => {
            p_addParam(acc, "[" + k + "]", val[k], usekey);
        });
    }
    else if (isArray(val)) {
        val.forEach((val) => {
            p_addParam(acc, "[]", val, usekey);
        });
    }
    else {
        acc.push(encodeURIComponent(usekey) + "=" + (isDefined(useval) ? encodeURIComponent(useval) : ""));
    }
}
/**
 * serializza un oggetto in forma url encoded
 *
 * @param {Object|Array} obj oggetto da serializzare
 * @return {string} stringa serializzata
 * @example
 *      var c = {href: "http://www.fanpage.it/rumors?utm_crash=123",
 *           sub: ["a","b","c","d"],
 *           nested: {
 *               href: "http://www.fanpage.it/subobj.zip?12=1&throw=14",
 *               name: "named",
 *               nested: {
 *                   href: "http://www.fanpage.it/subobj.zip?12=1&throw=14",
 *                   name: "named"
 *               }
 *           },
 *           label: "Articolo di Test", parameter: "zero"};
 *      var u = Utils.param(c);
 *      //u = http://www.fanpage.it%2Frumors%3Futm_crash%3D123&sub%5B%5D=a&sub%5B%5D=b&sub%5B%5D=c&sub%5B%5D=d&nested%5Bhref%5D=http%3A%2F%2Fwww.fanpage.it%2Fsubobj.zip%3F12%3D1%26throw%3D14&nested%5Bname%5D=named&nested%5Bnested%5D%5Bhref%5D=http%3A%2F%2Fwww.fanpage.it%2Fsubobj.zip%3F12%3D1%26throw%3D14&nested%5Bnested%5D%5Bname%5D=named&label=Articolo%20di%20Test&parameter=zero
 */
export function param(obj) {
    const acc = [];
    const uobj = p_execIfFunction(obj);
    if (isObject(uobj)) {
        Object.keys(uobj).forEach((k) => {
            p_addParam(acc, k, uobj[k], "");
        });
    }
    else if (isArray(uobj)) {
        uobj.map((val, idx) => {
            p_addParam(acc, "" + idx, val, "");
        });
    }
    else {
        acc.push(encodeURIComponent(uobj));
    }
    return acc.join("&");
}
function u_decodeKv(elem) {
    return elem.split("=", 2).map(decodeURIComponent);
}
function u_addParam(elements, prefix, arr) {
    // ? Questo any per quanto brutto serve ad evitare che rval.push non compili
    const rval = arr ? [] : {};
    let cont = true;
    while (cont === true) {
        cont = false;
        if (elements.length > 0) {
            const el = elements.pop();
            const kv = u_decodeKv(el);
            if (isArray(kv) && kv.length == 2) {
                if (isDefined(prefix) && kv[0].indexOf(prefix) == 0) {
                    cont = true;
                    if (prefix !== "") {
                        kv[0] = kv[0].substr(prefix.length);
                        kv[0] = kv[0].replace("[", "").replace("]", "");
                    }
                    if (arr) {
                        rval.push(kv[1]);
                    }
                    else {
                        const lpar = kv[0].indexOf("[");
                        if (lpar > 0) {
                            const rpar = kv[0].indexOf("]");
                            if (rpar > lpar) {
                                const propname = kv[0].substr(0, lpar);
                                elements.push(el);
                                rval[propname] = u_addParam(elements, (prefix ? prefix + "[" + propname + "]" : propname), (rpar == lpar + 1));
                            }
                        }
                        else
                            rval[kv[0]] = (kv[1] ? kv[1] : null);
                    }
                }
                else
                    elements.push(el);
            }
        }
    }
    return rval;
}
/**
 * DeSerializza una stringa che rappresenta un oggetto url-encoded e ne
 * fa l'unmarshalling in un oggetto. Inversa di `param`.
 *
 * @param {String} serialized oggetto serializzato url encoded
 * @return {Object} oggetto deserializzato
 *
 *
 */
export function unparam(serialized) {
    let obj = serialized;
    if (isString(serialized)) {
        const els = serialized.match(/[&]/) ?
            serialized.replace(/\+/g, "%20").split("&") :
            [serialized];
        if (els.length == 1) {
            const kv = u_decodeKv(els[0]);
            if (kv.length == 1)
                obj = kv[0];
            else {
                obj = {};
                obj[kv[0]] = kv[1];
            }
        }
        else if (els.length > 1) {
            obj = u_addParam(els.reverse(), "", false);
        }
    }
    return isDefined(obj) ? obj : {};
}
